import { useLazyQuery, useQuery } from '@apollo/client';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { Button, IconButton, InputAdornment, InputBase } from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import Toolbar from '@mui/material/Toolbar';
import type React from 'react';
import { type ChangeEvent, type MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Table } from 'src/Administration/Common';
import usersProfiles, { type UsersProfileNode, type UsersProfilesResponse } from 'src/api/profile/usersProfiles';
import AkomiDialog from 'src/components/dialogs/AkomiDialog';
import AkomiDialogActions from 'src/components/dialogs/AkomiDialogActions';
import { USER_ROLES } from 'src/constants';
import { useCustomToast } from 'src/hooks/menu';

export type Profile = UsersProfileNode;

const SELECT_ALL_HARD_CAP = 2000;
const SEARCH_DEBOUNCE_MS = 300;

interface RoleOption {
  label: string;
  value: string;
}

interface HeadCell {
  id: string;
  numeric: boolean;
  disablePadding: boolean;
  label: string;
}

interface TableCellValue {
  value: React.ReactNode;
  display: boolean;
}

type TableRow = TableCellValue[];

interface ProfileEdge {
  node: Profile;
}

export const headCellsAllUsersList: HeadCell[] = [
  { id: 'name', numeric: false, disablePadding: true, label: 'Username' },
  { id: 'Email', numeric: true, disablePadding: false, label: 'Email' },
  { id: 'First Name', numeric: true, disablePadding: false, label: 'First Name' },
  { id: 'Last Name', numeric: true, disablePadding: false, label: 'Last Name' },
  { id: 'Role', numeric: true, disablePadding: false, label: 'Role' },
];

export function convertAllUsersRows(data: ProfileEdge[]): TableRow[] {
  return data.map((a) => [
    { value: a.node.id, display: false },
    { value: a.node.user.username, display: true },
    { value: a.node.user.email, display: true },
    { value: a.node.firstName ?? '', display: true },
    { value: a.node.lastName ?? '', display: true },
    {
      value: (USER_ROLES as RoleOption[]).find(({ value }) => value === a.node.role)?.label ?? '',
      display: true,
    },
  ]);
}

interface AddUsersModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  isUserAlreadyAdded: (profileId: string) => boolean;
  onAddUsers: (profiles: Profile[]) => void;
}

export default function AddUsersModal({
  isModalOpen,
  setIsModalOpen,
  isUserAlreadyAdded,
  onAddUsers,
}: AddUsersModalProps): React.ReactElement {
  const { toastr } = useCustomToast();
  const [selectedUsersList, setSelectedUsersList] = useState<Profile[]>([]);
  const [searchVal, setSearchVal] = useState('');
  const [debouncedSearchVal, setDebouncedSearchVal] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    data: usersList,
    fetchMore,
    loading,
  } = useQuery<UsersProfilesResponse>(usersProfiles, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    variables: {
      first: rowsPerPage,
      last: rowsPerPage,
    },
  });

  const [fetchAllForSelect, { loading: selectAllLoading }] = useLazyQuery<UsersProfilesResponse>(usersProfiles, {
    fetchPolicy: 'network-only',
  });

  const count = usersList?.allProfiles.totalCount ?? 0;

  const selectedRowIds = useMemo(() => new Set(selectedUsersList.map((p) => p.id)), [selectedUsersList]);

  useEffect(() => {
    if (searchVal === debouncedSearchVal) return;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchVal(searchVal);
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchVal, debouncedSearchVal]);

  const onChangeSelect = (row: TableRow, e: ChangeEvent<HTMLInputElement>): void => {
    if (e.target.checked) {
      const found = usersList?.allProfiles.edges.find((item) => item.node.id === row[0].value);
      if (found) {
        setSelectedUsersList((rest) => [...rest, found.node]);
      }
    } else {
      setSelectedUsersList((rest) => rest.filter((v) => v.id !== row[0].value));
    }
  };

  const onClickAddUsers = (): void => {
    onAddUsers(selectedUsersList);
    setSelectedUsersList([]);
    setIsModalOpen(false);
  };

  const handleChangePage = (_event: MouseEvent<HTMLButtonElement> | null, newPage: number): void => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const usersRow = usersList
    ? convertAllUsersRows(usersList.allProfiles.edges.filter((a) => !isUserAlreadyAdded(a.node.id)))
    : [];

  const fetchMoreData = (howManyDeleted = 0): void => {
    const _count = count - howManyDeleted;
    const last = _count && (page + 1) * rowsPerPage > _count ? _count - page * rowsPerPage : rowsPerPage;

    fetchMore({
      variables: {
        first: (page + 1) * rowsPerPage,
        last: last,
        user_Username_Icontains: debouncedSearchVal || undefined,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult;
        return fetchMoreResult;
      },
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: fetchMoreData closes over count; only re-fetch when page, rowsPerPage, or debouncedSearchVal changes
  useEffect(() => {
    fetchMoreData();
  }, [page, rowsPerPage, debouncedSearchVal]);

  const loadProfilesForSelectAll = async (capped: number): Promise<Profile[] | null> => {
    const cachedEdges = usersList?.allProfiles.edges ?? [];
    if (cachedEdges.length >= capped) {
      return cachedEdges.slice(0, capped).map((e) => e.node);
    }
    try {
      const result = await fetchAllForSelect({
        variables: {
          first: capped,
          last: capped,
          user_Username_Icontains: debouncedSearchVal || undefined,
        },
      });
      if (result.error || !result.data) {
        toastr.error("Couldn't load users to select. Please try again.");
        return null;
      }
      return result.data.allProfiles.edges.map((e) => e.node);
    } catch {
      toastr.error("Couldn't load users to select. Please try again.");
      return null;
    }
  };

  const onSelectAll = async (): Promise<void> => {
    if (count === 0) return;
    const capped = Math.min(count, SELECT_ALL_HARD_CAP);
    const profiles = await loadProfilesForSelectAll(capped);
    if (!profiles) return;

    const eligible = profiles.filter((p) => !isUserAlreadyAdded(p.id));
    setSelectedUsersList((prev) => {
      const seen = new Set(prev.map((p) => p.id));
      return [...prev, ...eligible.filter((p) => !seen.has(p.id))];
    });
    if (count > SELECT_ALL_HARD_CAP) {
      toastr.info(
        `Selected the first ${SELECT_ALL_HARD_CAP.toLocaleString()} of ${count.toLocaleString()} matching users. You can add more after these by repeating with a refined search or after this batch.`,
      );
    }
  };

  const onClearSelection = (): void => {
    setSelectedUsersList([]);
  };

  const headerCheckboxChecked = selectedUsersList.length > 0 && selectedUsersList.length >= count && count > 0;
  const headerCheckboxIndeterminate = selectedUsersList.length > 0 && !headerCheckboxChecked;

  const onHeaderCheckboxChange = (): void => {
    if (headerCheckboxChecked) {
      onClearSelection();
    } else {
      void onSelectAll();
    }
  };

  const onClose = (): void => {
    setIsModalOpen(false);
    setSelectedUsersList([]);
  };

  return (
    <AkomiDialog
      open={isModalOpen}
      onClose={onClose}
      title="Users List"
      maxWidth="lg"
      actions={
        <AkomiDialogActions variant="cancel-confirm">
          <Button color="secondary" onClick={() => setIsModalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onClickAddUsers}
            disabled={selectedUsersList.length === 0}
          >
            {`Add users (${selectedUsersList.length})`}
          </Button>
        </AkomiDialogActions>
      }
    >
      <Toolbar sx={{ gap: 1.5, flexWrap: 'wrap' }}>
        <InputBase
          sx={{
            flex: '1 1 240px',
            maxWidth: 360,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            px: 1,
            py: 0.25,
          }}
          placeholder="Search username"
          inputProps={{ 'aria-label': 'Search username' }}
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          }
          endAdornment={
            searchVal ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Clear search" onClick={() => setSearchVal('')} sx={{ p: 0.25 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }
          onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchVal(e.target.value)}
          value={searchVal}
        />
      </Toolbar>

      <Table
        checkboxVisibility
        selectMode
        loading={loading}
        rowsPerPage={rowsPerPage}
        onChangeSelect={onChangeSelect}
        headCells={headCellsAllUsersList}
        rows={usersRow}
        selectedRowIds={selectedRowIds}
        headerCheckbox={{
          checked: headerCheckboxChecked,
          indeterminate: headerCheckboxIndeterminate,
          disabled: selectAllLoading || count === 0,
          onChange: onHeaderCheckboxChange,
          ariaLabel: 'Select all users',
        }}
      />
      <TablePagination
        sx={{ color: 'text.primary' }}
        rowsPerPageOptions={[25, 50, 100, 250]}
        component="div"
        count={count}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </AkomiDialog>
  );
}
